import React from "react";
import {
  isAndroidWebViewUserAgent,
  reportGlobalError,
} from "../globalErrorReporting";

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
}

/**
 * Último recurso contra tela branca: envolve o React root inteiro. Não
 * substitui ErrorBoundaries de domínio (ex.: `InspectionErrorBoundary`), que
 * continuam tratando falhas locais sem derrubar o app inteiro — este só entra
 * em ação quando o erro escapa de todos eles.
 */
export class GlobalErrorBoundary extends React.Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  state: GlobalErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): GlobalErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    reportGlobalError({
      type: "react",
      message: error.message || "Erro de renderização desconhecido",
      stack: error.stack ?? null,
      componentStack: errorInfo.componentStack ?? null,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isAndroidWebView = isAndroidWebViewUserAgent(
      typeof navigator === "undefined" ? null : navigator.userAgent,
    );

    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center bg-white px-4 py-10"
      >
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-8">
          <h1 className="text-lg font-semibold text-slate-900">
            Não foi possível carregar o sistema.
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Ocorreu um erro inesperado neste dispositivo.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-emerald-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800"
          >
            Recarregar página
          </button>
          {isAndroidWebView && (
            <p className="mt-4 text-xs text-slate-500">
              Se o problema continuar, abra o sistema diretamente no
              navegador.
            </p>
          )}
        </div>
      </div>
    );
  }
}
